import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTournaments } from '../context/TournamentContext';
import { enhancedSubscriptionManager } from '../context/shared/enhancedSubscriptionManager';

/**
 * Pending tournament result submissions awaiting the current user's review
 * (as a tournament owner). Mirrors the data used by the floating
 * TournamentNotifications widget, but exposes it so the header bell can
 * surface the same pending submissions alongside the other notifications.
 */
const useTournamentPendingSubmissions = () => {
  const { user } = useAuth();
  const { loadPendingTournamentSubmissions } = useTournaments();
  const [submissions, setSubmissions] = useState([]);

  const reload = useCallback(async () => {
    if (!user?.id) {
      setSubmissions([]);
      return;
    }
    try {
      const result = await loadPendingTournamentSubmissions();
      setSubmissions(result?.success ? (result.data || []) : []);
    } catch (error) {
      console.error('Error loading pending tournament submissions for bell:', error);
      setSubmissions([]);
    }
  }, [user?.id, loadPendingTournamentSubmissions]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Keep the bell in sync with dual-submission activity in real time.
  useEffect(() => {
    if (!user?.id) return;
    const key = `bell_tournament_submissions_${user.id}`;
    const handler = () => reload();
    enhancedSubscriptionManager.subscribe(
      key,
      { event: { event: '*', schema: 'public', table: 'tournament_match_submissions' }, rateLimitMs: 2000 },
      handler
    );
    enhancedSubscriptionManager.subscribe(
      `${key}_actions`,
      { event: { event: '*', schema: 'public', table: 'tournament_match_actions' }, rateLimitMs: 2000 },
      handler
    );
    return () => {
      enhancedSubscriptionManager.unsubscribe(key);
      enhancedSubscriptionManager.unsubscribe(`${key}_actions`);
    };
  }, [user?.id, reload]);

  return { submissions, count: submissions.length, reload };
};

export default useTournamentPendingSubmissions;
